import axios from "axios";
import { i18n } from "../translate/i18n";
import toastError from "../errors/toastError";
import {
  invalidateSession,
  registerApiCleanupHandler,
} from "./sessionInvalidation";
import { readToken, writeToken } from "./tokenStorage";
import { isPublicRoute } from "./publicRoutes";

let isRefreshing = false;
let failedRequestsQueue = [];
let isHandlingSessionInvalid = false;
let lastToastMessage = null;
let lastToastAt = 0;

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  withCredentials: true,
});

export const openApi = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
});

const TOAST_DEDUP_WINDOW_MS = 2500;
registerApiCleanupHandler(() => {
  api.defaults.headers.Authorization = undefined;
  openApi.defaults.headers.Authorization = undefined;
});

const showToastOnce = (message) => {
  const now = Date.now();

  if (
    !message ||
    (lastToastMessage === message && now - lastToastAt < TOAST_DEDUP_WINDOW_MS)
  ) {
    return;
  }

  lastToastMessage = message;
  lastToastAt = now;
  toastError(message);
};

const handleSessionInvalid = (reason = "unknown") => {
  if (isHandlingSessionInvalid) {
    return;
  }

  isHandlingSessionInvalid = true;

  failedRequestsQueue.forEach((request) =>
    request.reject(new axios.Cancel(`Session invalid: ${reason}`)),
  );
  failedRequestsQueue = [];



  showToastOnce(i18n.t("api.unauthorized") || "Sessão expirada");
  invalidateSession({ reason });
};

api.interceptors.request.use(
  (config) => {
    const token = readToken();
    const companyId = localStorage.getItem("companyId");

    if (!token && !config.url.includes("/auth")) {
      const message = i18n.t("api.unauthenticated");

      // Public screens (login/signup) mount providers before a session exists:
      // a missing token there is not an expired session.
      if (isPublicRoute(window.location.pathname)) {
        return Promise.reject(new axios.Cancel(message));
      }

      console.warn(
        `[API] Usuário sem token tentou chamar: ${config.method.toUpperCase()} ${config.url}`,
      );
      console.warn(`[API] Mensagem: ${message}`);
      console.trace(
        "[API] Rastreando stack de onde veio a requisição sem token",
      );

      handleSessionInvalid("missing token");
      return Promise.reject(new axios.Cancel(message));
    }

    // Se houver token, configura header Authorization
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (companyId) {
      config.headers["x-company-id"] = companyId;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    const originalRequest = error.config || {};

    const status = error.response?.status;
    const url = originalRequest.url || "<url desconhecida>";
    const method = (originalRequest.method || "GET").toUpperCase();
    const isRefreshRoute = url.includes("/auth/refresh_token");

    const backendError = error.response?.data?.error;
    
    const backendMessage = String(
      error.response?.data?.message || error.message || "",
    ).toLowerCase();

    const isSessionExpired =
      backendError === "ERR_SESSION_EXPIRED" ||
      backendMessage.includes("jwt expired");

    const irrecoverableAuthErrors = [
      "ERR_REFRESH_TOKEN_INVALID",
      "ERR_REFRESH_TOKEN_MISSING",
      "ERR_REFRESH_TOKEN_EXPIRED",
      "ERR_SESSION_INVALID",
      "ERR_UNAUTHENTICATED",
    ];

    const isUnauthorizedStatus = status === 401 || status === 403;
    const isIrrecoverableSessionError =
      isUnauthorizedStatus && irrecoverableAuthErrors.includes(backendError);

    const isRecoverableAuthError =
      isUnauthorizedStatus &&
      isSessionExpired &&
      !originalRequest._retry &&
      !isRefreshRoute;

    if (isIrrecoverableSessionError) {
      handleSessionInvalid(`session expired: ${method} ${url}`);
      return Promise.reject(error);
    }

    const shouldTryRefresh = isRecoverableAuthError;

    if (shouldTryRefresh) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedRequestsQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await api.post("/auth/refresh_token");

        if (data?.token) {
          writeToken(data.token);
          api.defaults.headers.Authorization = `Bearer ${data.token}`;

          failedRequestsQueue.forEach((request) => request.resolve(data.token));
          failedRequestsQueue = [];

          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${data.token}`;
        }

        return api(originalRequest);
      } catch (refreshError) {
        failedRequestsQueue.forEach((request) => request.reject(refreshError));
        failedRequestsQueue = [];

        handleSessionInvalid("refresh token failure");

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (status === 403 && !isRecoverableAuthError) {
      const message = isSessionExpired
        ? i18n.t("api.unauthorized") || "Sessão expirada (403)"
        : i18n.t("api.forbidden") || "Proibido (403)";
      showToastOnce(message);

      console.error(`[API] Requisição retornou 403: ${method} ${url}`);
      console.trace("[API] Stack trace da requisição que retornou 403");

      return Promise.reject(error);
    }

    // Para outros tipos de erro (500, 404, etc.), só rejeita normalmente para que o componente trate.
    return Promise.reject(error);
  },
);

export default api;
